/**
 * Public content layer — READ ONLY, lite SDK.
 *
 * Every public page reads its data through this module. It exists to keep
 * the full Firestore SDK, Cloud Storage and Firebase Auth out of the main
 * bundle (see firebase/config.js for the admin-side counterpart).
 *
 * Two responsibilities:
 *
 *  1. One-shot reads via `firebase/firestore/lite`.
 *
 *  2. A build-time state cache. During `react-snap` pre-rendering we record
 *     every document we fetched and serialise it into the generated HTML
 *     (`window.__UDENT_STATE__`). At runtime the client reads that snapshot
 *     as its FIRST render value, which means:
 *       • the pre-rendered markup already contains real content (SEO), and
 *       • `hydrateRoot` matches it exactly — no mismatch, no content flash,
 *         and no blocking Firestore round-trip before first paint.
 *     A background revalidation still runs so stale content self-heals.
 */

import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    doc,
    getDoc,
} from 'firebase/firestore/lite';
import { liteDb } from '../firebase/liteDb';
import { DEFAULT_SETTINGS } from './settingsDefaults';

export const SETTINGS_KEY = 'settings';

// Snapshot embedded into the pre-rendered HTML by react-snap (see index.js).
const PRELOADED = (typeof window !== 'undefined' && window.__UDENT_STATE__) || {};

// Everything fetched during this page's lifetime, keyed by collection name.
// Doubles as the payload react-snap serialises at build time.
const cache = { ...PRELOADED };

/** Read the preloaded/cached value for a key, or undefined when absent. */
export const getCached = (key) => cache[key];

/** The full cache — serialised into the HTML during pre-rendering. */
export const getStateSnapshot = () => cache;

// `id` is spread LAST so a stored `id` field can never shadow the real
// document ID (which React keys and every update/delete call depend on).
const mapDocs = (snapshot) =>
    snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));

/**
 * Fetch active items from a collection (isActive == true, ordered by `order`).
 *
 * Falls back to an unfiltered read + client-side filter/sort when the
 * composite index (isActive ASC, order ASC) hasn't finished building.
 * Any OTHER failure (permission-denied, unavailable, ...) is rethrown: the
 * fallback would issue a second, larger read that is equally doomed, and
 * callers need the real error to show their error UI instead of a spinner.
 */
export async function fetchActive(collectionName) {
    let items;
    try {
        const snapshot = await getDocs(query(
            collection(liteDb, collectionName),
            where('isActive', '==', true),
            orderBy('order'),
        ));
        items = mapDocs(snapshot);
    } catch (error) {
        // Firestore reports a missing/building composite index as
        // 'failed-precondition'. Everything else is a genuine failure.
        if (error.code !== 'failed-precondition') throw error;
        console.warn(
            `Composite index not ready for "${collectionName}", falling back to client-side filter:`,
            error.message,
        );
        const snapshot = await getDocs(collection(liteDb, collectionName));
        items = mapDocs(snapshot)
            .filter((item) => item.isActive !== false)
            .sort((first, second) => (first.order || 0) - (second.order || 0));
    }
    cache[collectionName] = items;
    return items;
}

/** Fetch the singleton clinic settings document, falling back to defaults. */
export async function fetchSettings() {
    const snap = await getDoc(doc(liteDb, 'settings', 'clinic'));
    const data = snap.exists() ? { ...DEFAULT_SETTINGS, ...snap.data() } : DEFAULT_SETTINGS;
    cache[SETTINGS_KEY] = data;
    return data;
}

// Named collection helpers — keep call sites free of magic strings.
export const SERVICES = 'services';
export const GALLERY = 'gallery';
export const TEAM = 'team';

export const fetchServices = () => fetchActive(SERVICES);
export const fetchGallery = () => fetchActive(GALLERY);
export const fetchTeam = () => fetchActive(TEAM);
